import { watch, computed, unref } from 'vue'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { $axios, $queryClient } from '@/boot/initApi'

const QUERY_KEY = ['fav-groups']
// Backend validates `items` with max:2000 on the batch endpoint.
const BATCH_SIZE = 2000

const invalidate = () => $queryClient.invalidateQueries({ queryKey: QUERY_KEY })

export default ({ groups, currentGroup }) => ({
  indexGroups: ({ enabled = true } = {}) => {
    const query = useQuery({
      queryKey: QUERY_KEY,
      queryFn: async () => {
        const { data } = await $axios.get('/api/fav-groups')
        return data ?? []
      },
      enabled,
      staleTime: 1000 * 60 * 5
    }, $queryClient)

    watch(query.data, val => {
      if (val) groups.value = val
    }, { immediate: true })

    return query
  },

  showGroup: ({ id, enabled = true } = {}) => {
    const query = useQuery({
      queryKey: computed(() => [...QUERY_KEY, unref(id)]),
      queryFn: async () => {
        const { data } = await $axios.get(`/api/fav-groups/${unref(id)}`)
        return data?.id ? data : null
      },
      enabled,
      staleTime: 1000 * 60 * 5
    }, $queryClient)

    watch(query.data, val => {
      if (val) currentGroup.value = val
    }, { immediate: true })

    return query
  },

  storeGroup: () => useMutation({
    mutationFn: ({ name, description }) => $axios.post('/api/fav-groups', { name, description }),
    onSuccess: invalidate
  }, $queryClient),

  updateGroup: () => useMutation({
    mutationFn: ({ id, name, description }) => $axios.put(`/api/fav-groups/${id}`, { name, description }),
    onSuccess: invalidate
  }, $queryClient),

  destroyGroup: () => useMutation({
    mutationFn: id => $axios.delete(`/api/fav-groups/${id}`),
    onSuccess: invalidate
  }, $queryClient),

  // One request for many items (chunked under the backend's per-request cap).
  // The response is the full group (same shape as showGroup), so the last one
  // is written straight into that query's cache instead of refetching; only the
  // list is invalidated, for its item counts. The detail key is String()-ed
  // because showGroup is keyed by the `:groupId` route param.
  addItems: () => useMutation({
    mutationFn: async ({ groupId, items }) => {
      let group = null
      for (let start = 0; start < items.length; start += BATCH_SIZE) {
        const { data } = await $axios.post(`/api/fav-groups/${groupId}/items/batch`, {
          items: items.slice(start, start + BATCH_SIZE)
        })
        group = data
      }
      return group
    },
    onSuccess: (group, { groupId }) => {
      $queryClient.setQueryData([...QUERY_KEY, String(groupId)], group?.id ? group : null)
      $queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: true })
    },
    // Earlier chunks may have landed before one failed.
    onError: invalidate
  }, $queryClient),

  removeItem: () => useMutation({
    mutationFn: ({ groupId, itemId }) => $axios.delete(`/api/fav-groups/${groupId}/items/${itemId}`),
    onSuccess: invalidate
  }, $queryClient),

  reorder: () => useMutation({
    mutationFn: orderedIds => $axios.patch('/api/fav-groups/order', { groups: orderedIds }),
    onSuccess: invalidate
  }, $queryClient)
})
